import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { userPreferencesService } from '../services/userPreferences';
import { timezoneGroupsService } from '../services/timezoneGroups';
import { guestSessionService } from '../services/guestSession';

export const useUserPreferences = () => {
  const { isAuthenticated, userId, loading: authLoading } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Separate state for timezone groups (not stored in localStorage)
  const [activeGroupTimezones, setActiveGroupTimezones] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);

  // Set when the timezone-groups fetch itself fails (401/500/network),
  // so the UI can show an error state instead of "no groups".
  const [groupsLoadError, setGroupsLoadError] = useState(null);
  const [groupsReloadCounter, setGroupsReloadCounter] = useState(0);
  
  // State for guest session timezones
  const [guestTimezones, setGuestTimezones] = useState([]);
  


  // Clear localStorage for authenticated users only on first load to avoid conflicts with timezone groups
  useEffect(() => {
    if (isAuthenticated && userId) {
      // Clear timezone-related localStorage data for authenticated users to prevent conflicts
      Object.keys(localStorage).forEach(key => {
        if ((key.includes('zonewise') || key.includes('selectedCities') || key.includes('timezone')) && 
            !key.includes('activeGroupId')) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [isAuthenticated, userId]);

  // Load preferences when auth state changes
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use userId for authenticated users, null for anonymous
        const userKey = isAuthenticated ? userId : null;
        const prefs = userPreferencesService.getPreferences(userKey);
        
        setPreferences(prefs);
        
        // If user is authenticated, load the active timezone group
        if (isAuthenticated && userId) {
          try {
            setGroupsLoadError(null);
            // First check localStorage for the active group ID
            const storedActiveGroupId = localStorage.getItem(`activeGroupId_${userId}`);
            
            if (storedActiveGroupId) {
              // We have a stored active group ID, just load that group's data
              setActiveGroupId(storedActiveGroupId);
              
              // Get all groups and find the active one
              const allGroups = await timezoneGroupsService.getAllGroups();
              const activeGroup = allGroups.find(g => g.id === storedActiveGroupId);
              
              if (activeGroup) {
                setActiveGroupTimezones(activeGroup.cities || []);
              } else {
                // Group not found, clear localStorage
                localStorage.removeItem(`activeGroupId_${userId}`);
                setActiveGroupId(null);
                setActiveGroupTimezones([]);
              }
            } else {
              // No stored active group, check for default zone
              const defaultZoneId = localStorage.getItem(`defaultZoneId_${userId}`);
              
              if (defaultZoneId) {
                // Load the default zone
                const allGroups = await timezoneGroupsService.getAllGroups();
                const defaultGroup = allGroups.find(g => g.id === defaultZoneId);
                
                if (defaultGroup) {
                  setActiveGroupId(defaultGroup.id);
                  setActiveGroupTimezones(defaultGroup.cities || []);
                  // Store as active for next time
                  localStorage.setItem(`activeGroupId_${userId}`, defaultGroup.id);
                } else {
                  // Default zone not found, load first available zone
                  if (allGroups.length > 0) {
                    const firstGroup = allGroups[0];
                    setActiveGroupId(firstGroup.id);
                    setActiveGroupTimezones(firstGroup.cities || []);
                    localStorage.setItem(`activeGroupId_${userId}`, firstGroup.id);
                  } else {
                    setActiveGroupId(null);
                    setActiveGroupTimezones([]);
                  }
                }
              } else {
                // No default zone set, load first available zone
                const allGroups = await timezoneGroupsService.getAllGroups();
                if (allGroups.length > 0) {
                  const firstGroup = allGroups[0];
                  setActiveGroupId(firstGroup.id);
                  setActiveGroupTimezones(firstGroup.cities || []);
                  localStorage.setItem(`activeGroupId_${userId}`, firstGroup.id);
                  // Set first zone as default if none exists
                  localStorage.setItem(`defaultZoneId_${userId}`, firstGroup.id);
                } else {
                  setActiveGroupId(null);
                  setActiveGroupTimezones([]);
                }
              }
            }
          } catch (error) {
            console.error('Error loading active timezone group on startup:', error.message);
            // Fetch failed (401/500/network) — surface an error state instead of
            // pretending the user has no groups. Keep the stored activeGroupId_*
            // key so a transient failure doesn't wipe the user's selection.
            setGroupsLoadError(error.message || 'Failed to load timezone groups');
            setActiveGroupId(null);
            setActiveGroupTimezones([]);
          }
        } else {
          // Non-authenticated user - initialize guest session
          const guestCities = guestSessionService.initializeSession();
          setGuestTimezones(guestCities);
        }
      } catch (err) {
        console.error('Error loading preferences:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadPreferences();
    }
  }, [isAuthenticated, userId, authLoading, groupsReloadCounter]);

  // Re-run the preferences/groups load after a failed fetch.
  const retryLoadGroups = useCallback(() => {
    setGroupsReloadCounter((n) => n + 1);
  }, []);

  // Migrate anonymous preferences when user signs in
  useEffect(() => {
    if (isAuthenticated && userId) {
      const migrated = userPreferencesService.migrateAnonymousToUser(userId);
      if (migrated) {
        // Reload preferences after migration
        const userPrefs = userPreferencesService.getPreferences(userId);
        setPreferences(userPrefs);
      }
    }
  }, [isAuthenticated, userId]);

  // Clear timezone group state when user actually signs out (not just auth flickers)
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      // Only clear if auth is stable and user is definitely not authenticated
      const clearTimer = setTimeout(() => {
        if (!isAuthenticated) {
          setActiveGroupTimezones([]);
          setActiveGroupId(null);
          // Clear localStorage for all users (we don't know which user just signed out)
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('activeGroupId_')) {
              localStorage.removeItem(key);
            }
          });
        }
      }, 1000); // Wait 1 second to avoid clearing on auth flickers
      
      return () => clearTimeout(clearTimer);
    }
  }, [isAuthenticated, authLoading]);

  const updatePreferences = useCallback((updates) => {
    try {
      const userKey = isAuthenticated ? userId : null;
      const updated = userPreferencesService.updatePreferences(updates, userKey);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const addTimezone = useCallback((city) => {
    try {
      // For authenticated users, don't use localStorage - this should update the active group instead
      if (isAuthenticated) {
        // For authenticated users, this shouldn't be called - timezone groups handle it
        return { selectedTimezones: activeGroupTimezones };
      }
      
      // For non-authenticated users, use guest session
      const updatedCities = guestSessionService.addTimezone(city);
      setGuestTimezones(updatedCities);
      return { selectedTimezones: updatedCities };
    } catch (err) {
      console.error('Error adding timezone:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, activeGroupTimezones]);

  const removeTimezone = useCallback((cityName) => {
    try {
      // For authenticated users, don't use localStorage - this should update the active group instead
      if (isAuthenticated) {
        // For authenticated users, this shouldn't be called - timezone groups handle it
        return { selectedTimezones: activeGroupTimezones };
      }
      
      // For non-authenticated users, use guest session
      const updatedCities = guestSessionService.removeTimezone(cityName);
      setGuestTimezones(updatedCities);
      return { selectedTimezones: updatedCities };
    } catch (err) {
      console.error('Error removing timezone:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, activeGroupTimezones]);

  const setHomeTimezone = useCallback((city) => {
    try {
      const userKey = isAuthenticated ? userId : null;
      const updated = userPreferencesService.setHomeTimezone(city, userKey);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error setting home timezone:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const setTimeFormat = useCallback((format) => {
    try {
      const userKey = isAuthenticated ? userId : null;
      const updated = userPreferencesService.setTimeFormat(format, userKey);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error setting time format:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const setLanguage = useCallback((language) => {
    try {
      const userKey = isAuthenticated ? userId : null;
      const updated = userPreferencesService.setLanguage(language, userKey);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error setting language:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const setDarkMode = useCallback((enabled) => {
    try {
      const userKey = isAuthenticated ? userId : null;
      const updated = userPreferencesService.setDarkMode(enabled, userKey);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error setting dark mode:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const clearPreferences = useCallback(() => {
    try {
      const userKey = isAuthenticated ? userId : null;
      userPreferencesService.clearPreferences(userKey);
      setPreferences(userPreferencesService.getPreferences(userKey));
      
      // Also clear timezone group state
      setActiveGroupTimezones([]);
      setActiveGroupId(null);
    } catch (err) {
      console.error('Error clearing preferences:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const exportPreferences = useCallback(() => {
    try {
      const userKey = isAuthenticated ? userId : null;
      return userPreferencesService.exportPreferences(userKey);
    } catch (err) {
      console.error('Error exporting preferences:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  const importPreferences = useCallback((data) => {
    try {
      const userKey = isAuthenticated ? userId : null;
      const updated = userPreferencesService.importPreferences(data, userKey);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error importing preferences:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  // City Preset Methods
  const getPresets = useCallback(async () => {
    try {
      // Let the service get its own fresh token
      return await timezoneGroupsService.getAllGroups();
    } catch (err) {
      console.error('Error getting presets:', err);
      setError(err.message);
      // Rethrow so callers can show an error state instead of an empty list
      throw err;
    }
  }, []);

  const addPreset = useCallback(async (name, cities) => {
    try {
      // Let the service get its own fresh token
      const result = await timezoneGroupsService.createGroup(name, cities);
      return result;
    } catch (err) {
      console.error('Error adding preset:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const updatePreset = useCallback(async (presetId, updates) => {
    try {
      // Let the service get its own fresh token
      const result = await timezoneGroupsService.updateGroup(presetId, updates);
      return result;
    } catch (err) {
      console.error('Error updating preset:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const deletePreset = useCallback(async (presetId) => {
    try {
      // Let the service get its own fresh token
      const result = await timezoneGroupsService.deleteGroup(presetId);
      return result;
    } catch (err) {
      console.error('Error deleting preset:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const loadPreset = useCallback(async (presetId, groupData = null) => {
    try {
      // Update the UI immediately
      setActiveGroupId(presetId);
      
      // Store in localStorage for fast access next time
      if (userId) {
        localStorage.setItem(`activeGroupId_${userId}`, presetId);
      }
      
      // If group data was passed, use it directly (no API call needed)
      if (groupData) {
        setActiveGroupTimezones(groupData.cities || []);
        
        // No need to set active in database - localStorage handles this
        
        return { selectedTimezones: groupData.cities || [], activeGroupId: presetId };
      }
      
      // Fallback: fetch the group if not provided
      const allGroups = await timezoneGroupsService.getAllGroups();
      const targetGroup = allGroups.find(g => g.id === presetId);
      
      if (targetGroup) {
        setActiveGroupTimezones(targetGroup.cities || []);
        
        // No need to set active in database - localStorage handles this
        
        return { selectedTimezones: targetGroup.cities || [], activeGroupId: targetGroup.id };
      } else {
        setActiveGroupTimezones([]);
        return { selectedTimezones: [], activeGroupId: presetId };
      }
    } catch (err) {
      console.error('Error loading preset:', err);
      setError(err.message);
      throw err;
    }
  }, [userId]);

  const setActiveGroup = useCallback(async (groupId) => {
    // Only update localStorage and local state - no database writes
    if (userId) {
      localStorage.setItem(`activeGroupId_${userId}`, groupId);
    }
    setActiveGroupId(groupId);
    
    return { activeGroupId: groupId };
  }, [userId]);

  const getActiveGroup = useCallback(async () => {
    // Only use localStorage - no database queries needed
    if (userId) {
      const storedActiveGroupId = localStorage.getItem(`activeGroupId_${userId}`);
      return storedActiveGroupId;
    }
    return null;
  }, [userId]);

  const hasChangesFromActiveGroup = useCallback(async (currentTimezones) => {
    try {
      // Let the service get its own fresh token
      const activeGroup = await timezoneGroupsService.getActiveGroup();
      return timezoneGroupsService.hasChangesFromActiveGroup(currentTimezones, activeGroup);
    } catch (err) {
      console.error('Error checking changes:', err);
      setError(err.message);
      return false;
    }
  }, []);

  const updateActiveGroupWithCurrentTimezones = useCallback(async (currentTimezones) => {
    try {
      // Update local state immediately for instant UI feedback
      setActiveGroupTimezones(currentTimezones);
      
      // Use the activeGroupId we already have instead of fetching it again
      if (!activeGroupId) {
        throw new Error('No active timezone group to update');
      }
      
      // Update the group in the database in the background
      const updatedGroup = await timezoneGroupsService.updateGroup(activeGroupId, {
        cities: currentTimezones.map(tz => ({
          name: tz.name,
          timezone: tz.timezone
        }))
      });
      
      return updatedGroup;
    } catch (err) {
      console.error('Error updating active group:', err);
      // Revert the optimistic update on error
      const activeGroup = await timezoneGroupsService.getActiveGroup();
      if (activeGroup) {
        setActiveGroupTimezones(activeGroup.cities || []);
      }
      setError(err.message);
      throw err;
    }
  }, [activeGroupId]);

  const reorderTimezones = useCallback((newOrder) => {
    try {
      if (isAuthenticated) {
        // For authenticated users, use the existing service
        const userKey = userId;
        const updated = userPreferencesService.reorderTimezones(newOrder, userKey);
        setPreferences(updated);
        return updated;
      }
      
      // For non-authenticated users, use guest session
      const updatedCities = guestSessionService.reorderTimezones(newOrder);
      setGuestTimezones(updatedCities);
      return { selectedTimezones: updatedCities };
    } catch (err) {
      console.error('Error reordering timezones:', err);
      setError(err.message);
      throw err;
    }
  }, [isAuthenticated, userId]);

  // Helper to clear timezone group state (useful for debugging)
  const clearTimezoneGroupState = useCallback(() => {
    setActiveGroupTimezones([]);
    setActiveGroupId(null);
  }, []);

  // Functions to manage default zone
  const setDefaultZone = useCallback((zoneId) => {
    if (userId) {
      localStorage.setItem(`defaultZoneId_${userId}`, zoneId);
      return true;
    }
    return false;
  }, [userId]);

  const getDefaultZone = useCallback(() => {
    if (userId) {
      return localStorage.getItem(`defaultZoneId_${userId}`);
    }
    return null;
  }, [userId]);

  const clearDefaultZone = useCallback(() => {
    if (userId) {
      localStorage.removeItem(`defaultZoneId_${userId}`);
      return true;
    }
    return false;
  }, [userId]);

  // Compute selectedTimezones
  // For authenticated users: use timezone group data
  // For non-authenticated users: use guest session data
  const computedSelectedTimezones = isAuthenticated ? activeGroupTimezones : guestTimezones;
  

  return {
    // State
    preferences,
    loading: loading || authLoading,
    error,

    // Computed values - for authenticated users, ONLY use timezone group data; no localStorage fallback  
    selectedTimezones: computedSelectedTimezones,
    timeFormat: preferences?.timeFormat || '12h',
    language: preferences?.language || 'en',
    homeTimezone: preferences?.homeTimezone || null,
    darkMode: preferences?.darkMode || false,
    cityPresets: preferences?.cityPresets || [],
    
    // Actions
    updatePreferences,
    addTimezone,
    removeTimezone,
    setHomeTimezone,
    setTimeFormat,
    setLanguage,
    setDarkMode,
    clearPreferences,
    exportPreferences,
    importPreferences,
    
    // Preset actions
    getPresets,
    addPreset,
    updatePreset,
    deletePreset,
    loadPreset,
    
    // Group actions
    setActiveGroup,
    getActiveGroup,
    hasChangesFromActiveGroup,
    updateActiveGroupWithCurrentTimezones,
    reorderTimezones,

    // Timezone group state
    activeGroupId,
    clearTimezoneGroupState,
    groupsLoadError,
    retryLoadGroups,
    
    // Default zone management
    setDefaultZone,
    getDefaultZone,
    clearDefaultZone,
    
    // Helpers
    hasPreferences: preferences && (
      preferences.selectedTimezones?.length > 0 ||
      preferences.homeTimezone ||
      preferences.language !== 'en' ||
      preferences.timeFormat !== '12h' ||
      preferences.darkMode
    ),
    
    // Auth context
    isAuthenticated,
    userId
  };
};

export default useUserPreferences;